import React, { useState, useEffect, useRef } from "react";

// ── Lotus — Mindful Setups brand mark: 7 petals, solid fill, no outline ──────
// Petal separation is cut out with a mask (transparent gaps) rather than a white stroke.
const LOTUS_PETALS = [
  "rotate(-82) scale(0.9 0.84)",
  "rotate(82) scale(0.9 0.84)",
  "rotate(-44) scale(0.98 0.98)",
  "rotate(44) scale(0.98 0.98)",
  "rotate(0) scale(0.9 0.96)",
  "rotate(-21) scale(1.08 0.9)",
  "rotate(21) scale(1.08 0.9)",
];
const IconLotus = ({ color }) => (
  <svg
    width="34"
    height="22"
    viewBox="0 0 167 110"
    fill={color}
    stroke="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <path
        id="one-lotus-petal"
        d="M0 0 C-24 -30, -23 -62, 0 -86 C23 -62, 24 -30, 0 0 Z"
      />
      <mask
        id="one-lotus-cut"
        maskUnits="userSpaceOnUse"
        x="-10"
        y="-10"
        width="187"
        height="130"
      >
        <rect x="-10" y="-10" width="187" height="130" fill="#ffffff" />
        <g
          transform="translate(84 91)"
          fill="#ffffff"
          stroke="#000000"
          strokeWidth="2.6"
          strokeLinejoin="round"
        >
          {LOTUS_PETALS.map((t) => (
            <use key={t} href="#one-lotus-petal" transform={t} />
          ))}
        </g>
      </mask>
    </defs>
    <g mask="url(#one-lotus-cut)">
      <g transform="translate(84 91)">
        {LOTUS_PETALS.map((t) => (
          <use key={t} href="#one-lotus-petal" transform={t} />
        ))}
      </g>
    </g>
  </svg>
);

// ── Yin-yang — proper filled yin (right) half ──────────────────────────────────
const IconYinYang = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 22 22">
    <path
      d="M11 1.5 A4.75 4.75 0 0 0 11 11 A4.75 4.75 0 0 1 11 20.5 A9.5 9.5 0 0 1 11 1.5Z"
      fill={color}
    />
    <circle cx="11" cy="6.25" r="1.65" fill={color} />
    <circle cx="11" cy="15.75" r="1.65" fill="rgba(245,240,230,0.95)" />
    <circle
      cx="11"
      cy="11"
      r="9.5"
      fill="none"
      stroke={color}
      strokeWidth="1.1"
    />
  </svg>
);

// ── Updated defaults ───────────────────────────────────────────────────────────
const DEFAULT_BOWEL_TAGS = ["Watery", "Blobs", "Lumps", "Pellets", "Stony"];
const DEFAULT_FOOD_TAGS = ["Dairy", "Tea", "Spicy"];
// A Food entry's meals. New entries carry breakfast/lunch/dinner (and optionally
// snacks) fields; older Food entries only have `description` and stay as they were.
const MEAL_FIELDS = [
  { id: "breakfast", label: "Breakfast" },
  { id: "lunch", label: "Lunch" },
  { id: "dinner", label: "Dinner" },
];
const NO_MEALS = { breakfast: "", lunch: "", dinner: "", snacks: "" };
const DEFAULT_SYMPTOM_CATS = [
  {
    id: "c_gen",
    name: "General",
    color: "#9a9080",
    tags: ["Fatigue", "Headache", "Nausea", "Pain", "Fever", "Dizziness"],
  },
  {
    id: "c_ulcer",
    name: "Ulcers",
    color: "#b88a80",
    tags: ["Mouth ulcer", "Abdominal", "Flare-up", "Healing", "Bleeding"],
  },
  {
    id: "c_fem",
    name: "Feminine Health",
    color: "#a88898",
    tags: ["Cramps", "Bloating", "Spotting", "Cycle day", "Heavy flow"],
  },
  {
    id: "c_energy",
    name: "Energy & Sleep",
    color: "#7a90a8",
    tags: ["Low energy", "Insomnia", "Brain fog", "Oversleeping"],
  },
  {
    id: "c_skin",
    name: "Skin",
    color: "#b09078",
    tags: ["Rash", "Dryness", "Itching", "Breakout", "Swelling"],
  },
];
const CAT_COLORS = [
  "#c08888", // muted red 1
  "#c49888", // muted red-orange
  "#c4a878", // muted orange
  "#c4b878", // muted yellow-orange
  "#b8c478", // muted yellow-green
  "#8b9a7f", // sage green (matches Bowel default)
  "#78b8a8", // muted teal
  "#78a8b8", // muted sky blue
  "#7898c8", // muted blue
  "#8888c4", // muted indigo
  "#9888c4", // muted blue-violet
  "#b088c4", // muted violet
  "#c488b8", // muted pink-purple
  "#c488a0", // muted pink
];

// Colour wheel: opens the device's native colour picker for any custom colour.
const WHEEL_BG =
  "conic-gradient(from 0deg,#d99a9a,#d9c08f,#b3c98f,#8cc4b8,#8fb0d9,#b79fd9,#d99ac0,#d99a9a)";
const ColorWheel = ({ value, onChange }) => {
  const custom = !CAT_COLORS.includes(value);
  return (
    <label
      title="Custom colour"
      style={{
        position: "relative",
        display: "block",
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        background: WHEEL_BG,
        cursor: "pointer",
        overflow: "hidden",
        border: custom
          ? "2.5px solid rgba(70,62,50,0.62)"
          : "1.5px solid rgba(105,138,98,0.24)",
        boxShadow: custom ? `0 0 6px ${value}80` : "none",
      }}
    >
      <input
        type="color"
        aria-label="Custom colour"
        value={/^#[0-9a-f]{6}$/i.test(value) ? value : "#8b9a7f"}
        onChange={(e) => onChange(e.target.value)}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          cursor: "pointer",
        }}
      />
    </label>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const toDK = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(dt.getDate()).padStart(2, "0")}`;
};
const startOfToday = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};
const fmtT = (iso) => {
  const d = new Date(iso);
  let h = d.getHours(),
    min = d.getMinutes(),
    ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${String(min).padStart(2, "0")} ${ap}`;
};
const fmtD = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};
const nowLocal = (base) => {
  const now = new Date();
  const d = base ? new Date(base) : now;
  if (base) d.setHours(now.getHours(), now.getMinutes(), 0, 0);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours()
  )}:${p(d.getMinutes())}`;
};

const TEXT = "#475d45",
  TEXT_SM = "#5a7157",
  TEXT_MU = "#7a9376";

// ── Swap helper ───────────────────────────────────────────────────────────────
const swapArr = (arr, i, j) => {
  if (j < 0 || j >= arr.length) return arr;
  const a = [...arr];
  [a[i], a[j]] = [a[j], a[i]];
  return a;
};

export default function App() {
  // ── Global ─────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("lotus");
  const [coverImage, setCoverImage] = useState("");
  const [showCoverMenu, setShowCoverMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 640
  );
  const coverMenuRef = useRef(null);
  const datePanelRef2 = useRef(null);
  // picker state: null | { mode:'month'|'year', year:number }
  const [picker1, setPicker1] = useState(null);
  const [picker2, setPicker2] = useState(null);

  // ── Tab 1 ──────────────────────────────────────────────────────────────────
  const [bowelEntries, setBowelEntries] = useState([]);
  const [bowelCats, setBowelCats] = useState([
    {
      id: "bc_bowel",
      name: "Bowel",
      color: "#8b9a7f",
      protected: true,
      tags: DEFAULT_BOWEL_TAGS,
    },
    {
      id: "bc_food",
      name: "Food",
      color: "#c9a87c",
      protected: true,
      tags: DEFAULT_FOOD_TAGS,
    },
  ]);
  const [editingBowelCat, setEditingBowelCat] = useState(null);
  const [showCatMgr1, setShowCatMgr1] = useState(false);
  const [newBowelCat, setNewBowelCat] = useState({
    name: "",
    color: "#8b9a7f",
  });
  const [expandBowelCatId, setExpandBowelCatId] = useState(null);
  const [bowelCatTagInputs, setBowelCatTagInputs] = useState({});
  const [calView1, setCalView1] = useState(new Date());
  const [selDate1, setSelDate1] = useState(startOfToday);
  const [listFilter1, setListFilter1] = useState(null); // null | 'bowel' | 'food'
  const [formOpen1, setFormOpen1] = useState(false);
  const [f1Desc, setF1Desc] = useState("");
  const [f1Additional, setF1Additional] = useState("");
  const [f1Tags, setF1Tags] = useState([]);
  const [f1Sev, setF1Sev] = useState(3);
  const [f1Time, setF1Time] = useState("");
  const [f1Meals, setF1Meals] = useState(NO_MEALS);
  const [f1Legacy, setF1Legacy] = useState(false); // editing a Food entry from before meals existed
  const [f1NewTag, setF1NewTag] = useState("");
  const [editingEntryId1, setEditingEntryId1] = useState(null);

  // ── Tab 2 ──────────────────────────────────────────────────────────────────
  const [symptomEntries, setSymptomEntries] = useState([]);
  const [symptomCats, setSymptomCats] = useState(DEFAULT_SYMPTOM_CATS);
  const [calView2, setCalView2] = useState(new Date());
  const [selDate2, setSelDate2] = useState(startOfToday);
  const [listFilter2, setListFilter2] = useState(null); // null | catId
  const [formOpen2, setFormOpen2] = useState(false);
  const [f2CatId, setF2CatId] = useState("c_gen");
  const [f2Desc, setF2Desc] = useState("");
  const [f2Treatment, setF2Treatment] = useState("");
  const [f2Tags, setF2Tags] = useState([]);
  const [f2Sev, setF2Sev] = useState(3);
  const [f2Time, setF2Time] = useState("");
  const [f2AllDay, setF2AllDay] = useState(true);
  const [f2NewTag, setF2NewTag] = useState("");
  const [showCatMgr, setShowCatMgr] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", color: "#8b9a7f" });
  const [expandCatId, setExpandCatId] = useState(null);
  const [editingEntryId2, setEditingEntryId2] = useState(null);
  const [editingCat, setEditingCat] = useState(null); // { id, name, color }
  const [catTagInputs, setCatTagInputs] = useState({});

  // ── Tag editing / reordering state ─────────────────────────────────────────
  // { listKey:'bowel'|'food'|catId, index:number, value:string }
  const [undoEntry1, setUndoEntry1] = useState(null); // {entry, timer}
  const [undoEntry2, setUndoEntry2] = useState(null);
  const [editingTag, setEditingTag] = useState(null);

  // ── CSS ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const s = document.createElement("style");
    s.textContent = `
      *,*::before,*::after{font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif !important;box-sizing:border-box;-webkit-tap-highlight-color:transparent;}
      html,body{overflow-x:hidden;width:100%;margin:0;padding:0;touch-action:pan-y;background:#dce6d5;}
      input,textarea,select{font-size:max(16px,0.875rem) !important;}
      select{-webkit-appearance:none;appearance:none;}
      @keyframes titleGlow{
        0%,100%{text-shadow:0 2px 14px rgba(30,20,10,0.14),0 -1px 10px rgba(30,20,10,0.08),0 0 10px rgba(255,255,255,0.88),0 0 28px rgba(255,255,255,0.48),0 0 60px rgba(255,248,230,0.26);}
        50%{text-shadow:0 2px 14px rgba(30,20,10,0.14),0 -1px 10px rgba(30,20,10,0.08),0 0 16px rgba(255,255,255,1),0 0 38px rgba(255,255,255,1),0 0 80px rgba(255,255,255,0.82),0 0 140px rgba(255,248,230,0.65),0 0 210px rgba(240,228,200,0.35);}
      }
      .app-title{animation:titleGlow 3s ease-in-out infinite;}
      @keyframes iconGlow{
        0%,100%{filter:drop-shadow(0 0 5px rgba(255,255,255,0.85)) drop-shadow(0 0 12px rgba(255,252,235,0.62)) drop-shadow(0 0 22px rgba(255,248,220,0.36));}
        50%{filter:drop-shadow(0 0 9px rgba(255,255,255,1)) drop-shadow(0 0 22px rgba(255,252,235,0.92)) drop-shadow(0 0 44px rgba(255,248,220,0.6)) drop-shadow(0 0 68px rgba(240,235,210,0.3));}
      }
      .icon-glow{animation:iconGlow 3s ease-in-out infinite;}
      .tab-btn{position:relative;border:none;background:transparent;cursor:pointer;}
      .tab-btn::after{content:'';position:absolute;inset:0;border-radius:40px;background:radial-gradient(ellipse 80% 70% at center,rgba(255,255,255,0.78) 0%,rgba(255,255,255,0.12) 55%,transparent 72%);opacity:0;transition:opacity 0.38s ease;pointer-events:none;}
      .tab-btn.active::after{opacity:0;}
      [style*="font-weight: 600"]{font-weight:500 !important;}
      .fern-bg{position:fixed;inset:0;z-index:0;overflow:hidden;pointer-events:none;}
      .fern-orb{position:absolute;border-radius:50%;will-change:transform;animation:fernDrift 28s ease-in-out infinite alternate;}
      .fern-orb.fo1{width:520px;height:520px;left:-200px;top:-160px;background:radial-gradient(circle,rgba(190,214,178,0.95),rgba(190,214,178,0) 68%);}
      .fern-orb.fo2{width:560px;height:560px;right:-260px;top:28%;background:radial-gradient(circle,rgba(228,240,214,0.9),rgba(228,240,214,0) 68%);animation-duration:34s;animation-delay:-9s;}
      .fern-orb.fo3{width:520px;height:520px;left:-120px;bottom:-240px;background:radial-gradient(circle,rgba(205,222,194,0.8),rgba(205,222,194,0) 68%);animation-duration:31s;animation-delay:-16s;}
      .fern-orb.fo4{width:380px;height:380px;right:-60px;bottom:6%;background:radial-gradient(circle,rgba(226,236,216,0.85),rgba(226,236,216,0) 68%);animation-duration:25s;animation-delay:-5s;}
      @keyframes fernDrift{0%{transform:translate3d(0,0,0) scale(1);}50%{transform:translate3d(36px,56px,0) scale(1.1);}100%{transform:translate3d(-28px,100px,0) scale(0.95);}}
      .tab-btn{transition:transform 0.25s cubic-bezier(.3,1.5,.5,1);}
      .tab-btn:active{transform:scale(0.9);}
      @media (prefers-reduced-motion:reduce){.fern-orb{animation:none;}}
      @keyframes slideDown{from{opacity:0;transform:translateY(-7px)}to{opacity:1;transform:translateY(0)}}
      .slide-down{animation:slideDown 0.22s ease-out forwards;}
      ::placeholder{color:rgba(160,148,130,0.38) !important;}
      input:focus,textarea:focus,select:focus{outline:none !important;box-shadow:0 0 0 1.5px rgba(139,154,127,0.42),0 0 8px rgba(139,154,127,0.1) !important;border-color:rgba(139,154,127,0.4) !important;}
      textarea{resize:none;}
      button{cursor:pointer;}
      .cal-cell{transition:background 0.1s;touch-action:manipulation;}
      .cal-cell:active{opacity:0.7;}
      .tag-pill{transition:all 0.12s;user-select:none;}
      .tag-pill:active{transform:scale(0.94);}
      .sev-dot{cursor:pointer;transition:transform 0.1s;}
      .sev-dot:active{transform:scale(1.3);}
      /* Hide CodeSandbox overlay */
      #csb-devtools,#csb-devtools-opener,
      [id^="csb-"],[class^="csb-"],
      [id*="sandbox"],[class*="open-sandbox"],
      iframe[src*="codesandbox"],
      a[href*="codesandbox.io"],
      div[style*="z-index: 99999"],
      div[style*="z-index:99999"]{display:none !important;pointer-events:none !important;}
    `;
    document.head.appendChild(s);
    let vp = document.querySelector('meta[name="viewport"]');
    if (!vp) {
      vp = document.createElement("meta");
      vp.name = "viewport";
      document.head.appendChild(vp);
    }
    vp.content = "width=device-width,initial-scale=1.0,viewport-fit=cover";

    // Hide CodeSandbox "Open Sandbox" overlay
    const hideCSB = () => {
      const selectors = [
        'iframe[src*="codesandbox"]',
        '[class*="open-in-sandbox"]',
        '[class*="openInSandbox"]',
        '[id*="sandbox-overlay"]',
        '[class*="sandbox-overlay"]',
        '[data-testid*="sandbox"]',
      ];
      selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          el.style.display = "none";
        });
      });
    };
    hideCSB();
    const observer = new MutationObserver(hideCSB);
    observer.observe(document.body, { childList: true, subtree: true });

    const onResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      observer.disconnect();
    };
  }, []);

  // Auto-scroll symptom date panel into view on mobile
  useEffect(() => {
    if (selDate2 && datePanelRef2.current) {
      setTimeout(
        () =>
          datePanelRef2.current?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          }),
        150
      );
    }
  }, [selDate2]);

  // Close cover menu on outside click
  useEffect(() => {
    if (!showCoverMenu) return;
    const h = (e) => {
      if (coverMenuRef.current && !coverMenuRef.current.contains(e.target))
        setShowCoverMenu(false);
    };
    document.addEventListener("mousedown", h);
    document.addEventListener("touchstart", h);
    return () => {
      document.removeEventListener("mousedown", h);
      document.removeEventListener("touchstart", h);
    };
  }, [showCoverMenu]);

  // ── localStorage ───────────────────────────────────────────────────────────
  useEffect(() => {
    const load = (k, fn, parse = true) => {
      const v = localStorage.getItem(k);
      if (v) fn(parse ? JSON.parse(v) : v);
    };
    load("one-cover", setCoverImage, false);
    load("one-bc", setBowelCats);
    // ── Backward-compat migration ─────────────────────────────────────────
    // Old entries stored category:'bowel' or category:'food'.
    // New system uses category:'bc_bowel' / category:'bc_food'.
    const rawBE = localStorage.getItem("one-be");
    if (rawBE) {
      const parsed = JSON.parse(rawBE);
      const migrated = parsed.map((e) => ({
        ...e,
        category:
          e.category === "bowel"
            ? "bc_bowel"
            : e.category === "food"
            ? "bc_food"
            : e.category,
      }));
      if (migrated.some((e, i) => e.category !== parsed[i].category)) {
        localStorage.setItem("one-be", JSON.stringify(migrated));
      }
      setBowelEntries(migrated);
    }
    load("one-se", setSymptomEntries);
    load("one-sc", setSymptomCats);
  }, []);

  const saveBE = (v) => {
    setBowelEntries(v);
    localStorage.setItem("one-be", JSON.stringify(v));
  };
  const saveBC = (v) => {
    setBowelCats(v);
    localStorage.setItem("one-bc", JSON.stringify(v));
  };
  const saveSE = (v) => {
    setSymptomEntries(v);
    localStorage.setItem("one-se", JSON.stringify(v));
  };
  const saveSC = (v) => {
    setSymptomCats(v);
    localStorage.setItem("one-sc", JSON.stringify(v));
  };

  // ── Cover ──────────────────────────────────────────────────────────────────
  const onCoverUpload = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const r = new FileReader();
    r.onloadend = () => {
      setCoverImage(r.result);
      localStorage.setItem("one-cover", r.result);
      setShowCoverMenu(false);
    };
    r.readAsDataURL(f);
    e.target.value = "";
  };
  const removeCover = () => {
    setCoverImage("");
    localStorage.removeItem("one-cover");
    setShowCoverMenu(false);
  };

  // ── Tab 1 actions ──────────────────────────────────────────────────────────
  const openForm1 = (cat) => {
    setFormOpen1(cat);
    setF1Meals(NO_MEALS);
    setF1Legacy(false);
    setF1Desc("");
    setF1Additional("");
    setF1Tags([]);
    setF1Sev(3);
    setF1NewTag("");
    setF1Time(nowLocal(selDate1));
  };
  const closeForm1 = () => {
    setFormOpen1(false);
    setEditingEntryId1(null);
  };
  const openEditForm1 = (entry) => {
    const d = new Date(entry.timestamp),
      p = (n) => String(n).padStart(2, "0");
    setEditingEntryId1(entry.id);
    setFormOpen1(entry.category);
    setF1Meals({
      breakfast: entry.breakfast || "",
      lunch: entry.lunch || "",
      dinner: entry.dinner || "",
      snacks: entry.snacks || "",
    });
    setF1Legacy(
      entry.category === "bc_food" && !MEAL_FIELDS.some((m) => m.id in entry)
    );
    setF1Desc(entry.description || "");
    setF1Additional(entry.additional || "");
    setF1Tags(entry.tags || []);
    setF1Sev(entry.severity || 3);
    setF1Time(
      `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
        d.getHours()
      )}:${p(d.getMinutes())}`
    );
    setF1NewTag("");
  };
  const toggleF1Tag = (t) =>
    setF1Tags((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));
  const addF1Tag = () => {
    const t = f1NewTag.trim();
    if (!t) return;
    saveBC(
      bowelCats.map((c) =>
        c.id === formOpen1 && !c.tags.includes(t)
          ? { ...c, tags: [...c.tags, t] }
          : c
      )
    );
    setF1Tags((p) => (p.includes(t) ? p : [...p, t]));
    setF1NewTag("");
  };
  const saveEntry1 = () => {
    // Meal fields apply to Food only. Legacy Food entries are never given the
    // breakfast/lunch/dinner split; snacks is only written if used.
    const mealPatch = (prev) => {
      if (formOpen1 !== "bc_food") return {};
      const p = {};
      if (!f1Legacy) MEAL_FIELDS.forEach((m) => (p[m.id] = f1Meals[m.id]));
      if (f1Meals.snacks || (prev && "snacks" in prev)) p.snacks = f1Meals.snacks;
      return p;
    };
    const ts = f1Time
      ? new Date(f1Time).toISOString()
      : new Date().toISOString();
    if (editingEntryId1) {
      saveBE(
        bowelEntries.map((e) =>
          e.id === editingEntryId1
            ? {
                ...e,
                timestamp: ts,
                category: formOpen1,
                description: f1Desc,
                additional: f1Additional,
                tags: f1Tags,
                severity: f1Sev,
                ...mealPatch(e),
              }
            : e
        )
      );
    } else {
      saveBE([
        ...bowelEntries,
        {
          id: Date.now() + "",
          timestamp: ts,
          category: formOpen1,
          description: f1Desc,
          additional: f1Additional,
          tags: f1Tags,
          severity: f1Sev,
          ...mealPatch(null),
        },
      ]);
    }
    closeForm1();
  };
  const delEntry1 = (id) => {
    const entry = bowelEntries.find((e) => e.id === id);
    saveBE(bowelEntries.filter((e) => e.id !== id));
    if (undoEntry1?.timer) clearTimeout(undoEntry1.timer);
    const timer = setTimeout(() => setUndoEntry1(null), 5000);
    setUndoEntry1({ entry, timer });
  };
  const undoDelete1 = () => {
    if (!undoEntry1) return;
    clearTimeout(undoEntry1.timer);
    saveBE([...bowelEntries, undoEntry1.entry]);
    setUndoEntry1(null);
  };

  const saveEditBowelCat = () => {
    if (!editingBowelCat || !editingBowelCat.name.trim()) {
      setEditingBowelCat(null);
      return;
    }
    saveBC(
      bowelCats.map((c) =>
        c.id === editingBowelCat.id
          ? {
              ...c,
              name: editingBowelCat.name.trim(),
              color: editingBowelCat.color,
            }
          : c
      )
    );
    setEditingBowelCat(null);
  };
  const addBowelCat = () => {
    if (!newBowelCat.name.trim()) return;
    saveBC([
      ...bowelCats,
      {
        id: "bc_" + Date.now(),
        name: newBowelCat.name.trim(),
        color: newBowelCat.color,
        protected: false,
        tags: [],
      },
    ]);
    setNewBowelCat({ name: "", color: "#8b9a7f" });
  };
  const addTagToBowelCat = (catId) => {
    const t = (bowelCatTagInputs[catId] || "").trim();
    if (!t) return;
    saveBC(
      bowelCats.map((c) =>
        c.id === catId ? { ...c, tags: [...c.tags, t] } : c
      )
    );
    setBowelCatTagInputs((p) => ({ ...p, [catId]: "" }));
  };
  const removeTagFromBowelCat = (catId, idx) =>
    saveBC(
      bowelCats.map((c) =>
        c.id === catId ? { ...c, tags: c.tags.filter((_, i) => i !== idx) } : c
      )
    );

  // ── Tab 2 actions ──────────────────────────────────────────────────────────
  const openForm2 = (catId) => {
    setFormOpen2(true);
    setF2CatId(catId);
    setF2Desc("");
    setF2Treatment("");
    setF2Tags([]);
    setF2Sev(3);
    setF2NewTag("");
    setF2AllDay(true);
    setF2Time("");
    setEditingEntryId2(null);
  };
  const closeForm2 = () => {
    setFormOpen2(false);
    setEditingEntryId2(null);
    setF2Treatment("");
  };
  const openEditForm2 = (entry) => {
    const d = new Date(entry.timestamp),
      p = (n) => String(n).padStart(2, "0");
    setEditingEntryId2(entry.id);
    setFormOpen2(true);
    setF2CatId(entry.categoryId);
    setF2Desc(entry.description || "");
    setF2Treatment(entry.treatment || "");
    setF2Tags(entry.tags || []);
    setF2Sev(entry.severity || 3);
    setF2AllDay(entry.allDay ?? true);
    setF2Time(
      entry.allDay
        ? ""
        : `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
            d.getHours()
          )}:${p(d.getMinutes())}`
    );
    setF2NewTag("");
  };
  const toggleF2Tag = (t) =>
    setF2Tags((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));
  const addF2Tag = () => {
    const t = f2NewTag.trim();
    if (!t) return;
    const cat = symptomCats.find((c) => c.id === f2CatId);
    if (cat && !cat.tags.includes(t))
      saveSC(
        symptomCats.map((c) =>
          c.id === f2CatId ? { ...c, tags: [...c.tags, t] } : c
        )
      );
    setF2Tags((p) => (p.includes(t) ? p : [...p, t]));
    setF2NewTag("");
  };
  const saveEntry2 = () => {
    let ts;
    if (f2AllDay) {
      // Store as noon SGT on the selected date so toDK() maps correctly
      const base = selDate2 ? toDK(selDate2) : toDK(new Date());
      ts = new Date(base + "T12:00:00").toISOString();
    } else {
      ts = f2Time ? new Date(f2Time).toISOString() : new Date().toISOString();
    }
    const entry = {
      categoryId: f2CatId,
      description: f2Desc,
      treatment: f2Treatment,
      tags: f2Tags,
      severity: f2Sev,
      allDay: f2AllDay,
      timestamp: ts,
    };
    if (editingEntryId2) {
      saveSE(
        symptomEntries.map((e) =>
          e.id === editingEntryId2 ? { ...e, ...entry } : e
        )
      );
    } else {
      saveSE([...symptomEntries, { id: Date.now() + "", ...entry }]);
    }
    closeForm2();
  };
  const delEntry2 = (id) => {
    const entry = symptomEntries.find((e) => e.id === id);
    saveSE(symptomEntries.filter((e) => e.id !== id));
    if (undoEntry2?.timer) clearTimeout(undoEntry2.timer);
    const timer = setTimeout(() => setUndoEntry2(null), 5000);
    setUndoEntry2({ entry, timer });
  };
  const undoDelete2 = () => {
    if (!undoEntry2) return;
    clearTimeout(undoEntry2.timer);
    saveSE([...symptomEntries, undoEntry2.entry]);
    setUndoEntry2(null);
  };
  const addSymptomCat = () => {
    if (!newCat.name.trim()) return;
    saveSC([
      ...symptomCats,
      {
        id: "c_" + Date.now(),
        name: newCat.name.trim(),
        color: newCat.color,
        tags: [],
      },
    ]);
    setNewCat({ name: "", color: "#8b9a7f" });
  };
  const deleteCat = (id) => saveSC(symptomCats.filter((c) => c.id !== id));
  const saveEditCat = () => {
    if (!editingCat || !editingCat.name.trim()) {
      setEditingCat(null);
      return;
    }
    saveSC(
      symptomCats.map((c) =>
        c.id === editingCat.id
          ? { ...c, name: editingCat.name.trim(), color: editingCat.color }
          : c
      )
    );
    setEditingCat(null);
  };
  const addTagToCat = (catId) => {
    const t = (catTagInputs[catId] || "").trim();
    if (!t) return;
    saveSC(
      symptomCats.map((c) =>
        c.id === catId ? { ...c, tags: [...c.tags, t] } : c
      )
    );
    setCatTagInputs((p) => ({ ...p, [catId]: "" }));
  };
  const removeTagFromCat = (catId, idx) =>
    saveSC(
      symptomCats.map((c) =>
        c.id === catId ? { ...c, tags: c.tags.filter((_, i) => i !== idx) } : c
      )
    );

  // ── Tag rename / reorder ────────────────────────────────────────────────────
  const startEdit = (listKey, index, value) =>
    setEditingTag({ listKey, index, value });
  const cancelEdit = () => setEditingTag(null);
  const saveEdit = () => {
    if (!editingTag) return;
    const { listKey, index, value } = editingTag;
    const trimmed = value.trim();
    if (!trimmed) {
      cancelEdit();
      return;
    }
    const isBowelCat = bowelCats.some((c) => c.id === listKey);
    if (isBowelCat)
      saveBC(
        bowelCats.map((c) =>
          c.id === listKey
            ? { ...c, tags: c.tags.map((t, i) => (i === index ? trimmed : t)) }
            : c
        )
      );
    else
      saveSC(
        symptomCats.map((c) =>
          c.id === listKey
            ? { ...c, tags: c.tags.map((t, i) => (i === index ? trimmed : t)) }
            : c
        )
      );
    cancelEdit();
  };
  const moveTag = (listKey, index, dir) => {
    const isBowelCat = bowelCats.some((c) => c.id === listKey);
    if (isBowelCat)
      saveBC(
        bowelCats.map((c) =>
          c.id === listKey
            ? { ...c, tags: swapArr(c.tags, index, index + dir) }
            : c
        )
      );
    else
      saveSC(
        symptomCats.map((c) =>
          c.id === listKey
            ? { ...c, tags: swapArr(c.tags, index, index + dir) }
            : c
        )
      );
  };

  // ── Export / Import ────────────────────────────────────────────────────────
  const exportData = () => {
    const data = {
      bowelEntries,
      bowelCats,
      symptomEntries,
      symptomCats,
      coverImage,
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `one-health-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const importData = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => {
      try {
        const d = JSON.parse(ev.target.result);
        if (d.bowelEntries) {
          const migrated = d.bowelEntries.map((e) => ({
            ...e,
            category:
              e.category === "bowel"
                ? "bc_bowel"
                : e.category === "food"
                ? "bc_food"
                : e.category,
          }));
          saveBE(migrated);
        }
        if (d.bowelCats) saveBC(d.bowelCats);
        // Handle legacy export that had bowelTags/foodTags instead of bowelCats
        if (!d.bowelCats && (d.bowelTags || d.foodTags)) {
          saveBC([
            {
              id: "bc_bowel",
              name: "Bowel",
              color: "#8b9a7f",
              protected: true,
              tags: d.bowelTags || DEFAULT_BOWEL_TAGS,
            },
            {
              id: "bc_food",
              name: "Food",
              color: "#c9a87c",
              protected: true,
              tags: d.foodTags || DEFAULT_FOOD_TAGS,
            },
          ]);
        }
        if (d.symptomEntries) saveSE(d.symptomEntries);
        if (d.symptomCats) saveSC(d.symptomCats);
        if (d.coverImage) {
          setCoverImage(d.coverImage);
          localStorage.setItem("one-cover", d.coverImage);
        }
        alert("✅ Data imported.");
      } catch {
        alert("❌ Import failed.");
      }
    };
    r.readAsText(f);
    e.target.value = "";
  };

  // ── Design tokens ──────────────────────────────────────────────────────────
  const PANEL = {
    background: coverImage
      ? "linear-gradient(160deg,rgba(255,252,246,0.58),rgba(255,252,246,0.42))"
      : "linear-gradient(160deg,rgba(255,252,246,0.34),rgba(255,252,246,0.08) 60%,rgba(255,252,246,0.18))",
    backdropFilter: coverImage
      ? "blur(22px) saturate(140%)"
      : "blur(12px) saturate(150%)",
    WebkitBackdropFilter: coverImage
      ? "blur(22px) saturate(140%)"
      : "blur(12px) saturate(150%)",
    borderRadius: "30px",
    padding: "20px",
    border: "1px solid rgba(255,255,255,0.6)",
    boxShadow:
      "0 20px 40px -26px rgba(65,100,55,0.38),0 1px 0 rgba(255,255,255,0.65) inset",
  };
  const INNER = {
    background: "rgba(255,255,255,0.2)",
    borderRadius: "22px",
    border: "1px solid rgba(255,255,255,0.4)",
    padding: "14px 16px",
  };
  const INPUT = {
    width: "100%",
    maxWidth: "100%",
    padding: "10px 13px",
    borderRadius: "11px",
    background: "rgba(255,255,255,0.32)",
    border: "1px solid rgba(255,255,255,0.5)",
    color: TEXT,
    lineHeight: "1.55",
    boxSizing: "border-box",
    whiteSpace: "pre-wrap",
  };
  const SELECT = {
    ...INPUT,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 5 5-5' stroke='%23998878' strokeWidth='1.4' fill='none'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    paddingRight: "30px",
  };

  const FLabel = ({ children }) => (
    <div
      style={{
        fontSize: "0.67rem",
        color: TEXT_SM,
        marginBottom: "6px",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        fontWeight: "500",
      }}
    >
      {children}
    </div>
  );

  // ── Tag row: view/edit/reorder/delete ──────────────────────────────────────
  const renderTagRow = (tag, index, total, listKey, color, onDelete) => {
    const isEditing =
      editingTag?.listKey === listKey && editingTag?.index === index;
    const iconBtn = (onClick, content, extraStyle = {}) => (
      <button
        type="button"
        onClick={onClick}
        style={{
          background: "none",
          border: "none",
          padding: "3px 5px",
          borderRadius: "5px",
          fontSize: "0.72rem",
          lineHeight: 1,
          color: color + "88",
          cursor: "pointer",
          ...extraStyle,
        }}
      >
        {content}
      </button>
    );

    if (isEditing) {
      return (
        <div
          key={index}
          style={{
            display: "flex",
            gap: "5px",
            alignItems: "center",
            marginBottom: "5px",
          }}
        >
          <input
            value={editingTag.value}
            onChange={(e) =>
              setEditingTag((p) => ({ ...p, value: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") saveEdit();
              if (e.key === "Escape") cancelEdit();
            }}
            autoFocus
            style={{
              ...INPUT,
              flex: 1,
              padding: "5px 9px",
              fontSize: "0.78rem",
            }}
          />
          <button
            type="button"
            onClick={saveEdit}
            style={{
              background: "none",
              border: "none",
              color: "#8b9a7f",
              fontSize: "0.82rem",
              padding: "3px 6px",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            ✓
          </button>
          <button
            type="button"
            onClick={cancelEdit}
            style={{
              background: "none",
              border: "none",
              color: TEXT_MU,
              fontSize: "0.82rem",
              padding: "3px 5px",
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>
      );
    }
    return (
      <div
        key={index}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "2px",
          marginBottom: "4px",
        }}
      >
        <span
          style={{
            flex: 1,
            padding: "3px 9px",
            borderRadius: "16px",
            background: color + "18",
            border: `1px solid ${color}30`,
            color,
            fontSize: "0.72rem",
            fontWeight: "500",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {tag}
        </span>
        {iconBtn(() => startEdit(listKey, index, tag), "✎")}
        {iconBtn(() => moveTag(listKey, index, -1), "↑", {
          opacity: index === 0 ? 0.2 : 1,
        })}
        {iconBtn(() => moveTag(listKey, index, 1), "↓", {
          opacity: index === total - 1 ? 0.2 : 1,
        })}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onDelete();
          }}
          style={{
            background: "none",
            border: "none",
            padding: "3px 5px",
            borderRadius: "5px",
            fontSize: "0.72rem",
            lineHeight: 1,
            color: "rgba(180,100,90,0.55)",
            cursor: "pointer",
          }}
        >
          ✕
        </button>
      </div>
    );
  };

  // ── Entry tag pill ─────────────────────────────────────────────────────────
  const renderPill = (label, on, color, onClick) => (
    <span
      key={label}
      className="tag-pill"
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 10px",
        borderRadius: "20px",
        cursor: "pointer",
        fontSize: "0.74rem",
        fontWeight: on ? "500" : "400",
        background: on ? color + "28" : "rgba(255,255,255,0.55)",
        border: on
          ? `1.5px solid ${color}52`
          : "1px solid rgba(105,138,98,0.26)",
        color: on ? color : TEXT_SM,
      }}
    >
      {on && (
        <span
          style={{
            width: "5px",
            height: "5px",
            borderRadius: "50%",
            background: color,
            flexShrink: 0,
          }}
        />
      )}
      {label}
    </span>
  );

  const renderSevPicker = (val, set, color = "#b88a80") => (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <div
          key={n}
          className="sev-dot"
          onClick={() => set(n)}
          style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            background: n <= val ? color : "rgba(105,138,98,0.22)",
            border: `1.5px solid ${n <= val ? color : "rgba(105,138,98,0.3)"}`,
            boxShadow: n <= val ? `0 0 5px ${color}52` : "none",
          }}
        />
      ))}
    </div>
  );

  const renderEntryCard = (entry, color, onDel, badge, onEdit) => (
    <div
      key={entry.id}
      style={{
        ...INNER,
        position: "relative",
        border: "none",
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
        padding: "13px 14px",
        marginBottom: "9px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "8px",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "5px",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{ fontSize: "0.68rem", color: TEXT_SM, fontWeight: "500" }}
            >
              {entry.allDay ? "All Day" : fmtT(entry.timestamp)}
            </span>
            {badge}
          </div>
          {entry.description && (
            <div
              style={{
                fontSize: "0.82rem",
                color: TEXT,
                lineHeight: "1.55",
                whiteSpace: "pre-wrap",
                marginBottom:
                  entry.additional ||
                  entry.treatment ||
                  entry.snacks ||
                  entry.tags?.length
                    ? "7px"
                    : 0,
              }}
            >
              {entry.description}
            </div>
          )}
          {[...MEAL_FIELDS, { id: "snacks", label: "Snacks" }].map((m) =>
            entry[m.id] ? (
              <div key={m.id} style={{ marginBottom: "7px" }}>
                <div
                  style={{
                    fontSize: "0.66rem",
                    color: TEXT_MU,
                    letterSpacing: "0.04em",
                    marginBottom: "1px",
                  }}
                >
                  {m.label}
                </div>
                <div
                  style={{
                    fontSize: "0.82rem",
                    color: TEXT,
                    lineHeight: "1.55",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {entry[m.id]}
                </div>
              </div>
            ) : null
          )}
          {entry.additional && (
            <div
              style={{
                fontSize: "0.76rem",
                color: TEXT_SM,
                lineHeight: "1.45",
                whiteSpace: "pre-wrap",
                fontStyle: "italic",
                marginBottom: entry.treatment || entry.tags?.length ? "7px" : 0,
              }}
            >
              {entry.additional}
            </div>
          )}
          {entry.treatment && (
            <div
              style={{
                fontSize: "0.76rem",
                color: TEXT_SM,
                lineHeight: "1.45",
                whiteSpace: "pre-wrap",
                fontStyle: "italic",
                marginBottom: entry.tags?.length ? "7px" : 0,
              }}
            >
              {entry.treatment}
            </div>
          )}
          {entry.tags?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {entry.tags.map((t, i) => (
                <span
                  key={i}
                  style={{
                    padding: "4px 10px",
                    borderRadius: "30px",
                    fontSize: "0.72rem",
                    background: "rgba(255,255,255,0.3)",
                    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.4)",
                    color: TEXT_SM,
                    fontWeight: "400",
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            gap: "2px",
            flexShrink: 0,
            alignItems: "flex-start",
          }}
        >
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit()}
              style={{
                background: "none",
                border: "none",
                color: "rgba(140,128,110,0.45)",
                fontSize: "0.8rem",
                padding: "2px 5px",
                borderRadius: "6px",
              }}
            >
              ✎
            </button>
          )}
          <button
            type="button"
            onClick={() => onDel()}
            style={{
              background: "none",
              border: "none",
              color: "rgba(160,144,126,0.38)",
              fontSize: "0.82rem",
              padding: "2px 5px",
              borderRadius: "6px",
            }}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );

  const renderSectionHead = (color, label, count) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        marginBottom: "8px",
      }}
    >
      <div
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          background: color,
        }}
      />
      <span
        style={{
          fontSize: "0.68rem",
          color,
          fontWeight: "600",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      {count > 0 && (
        <span
          style={{
            fontSize: "0.62rem",
            color: TEXT_SM,
            background: "rgba(105,138,98,0.16)",
            padding: "1px 5px",
            borderRadius: "8px",
          }}
        >
          {count}
        </span>
      )}
    </div>
  );

  // ── Calendar 1 ────────────────────────────────────────────────────────────
  // ── Month / Year picker (shared by both calendars) ───────────────────────
  const renderPicker = (picker, setPicker, calView, setCalView) => {
    const s = new Date().toLocaleDateString("en-CA", {
      timeZone: "Asia/Singapore",
    });
    const [cy, cm] = s.split("-").map(Number);
    const curYear = cy,
      curMonth = cm - 1;
    const MTHS = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const acc = "rgba(139,154,127,0.25)",
      accB = "1.5px solid rgba(139,154,127,0.45)";
    const btn = (extra = {}) => ({
      borderRadius: "9px",
      fontSize: "0.8rem",
      cursor: "pointer",
      color: TEXT,
      border: "1px solid rgba(105,138,98,0.22)",
      background: "rgba(255,255,255,0.35)",
      ...extra,
    });

    if (picker.mode === "month") {
      const canPrev = picker.year > 2020,
        canNext = picker.year < curYear;
      return (
        <div style={{ padding: "4px 0" }} className="slide-down">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <button
              onClick={() => setPicker((p) => ({ ...p, year: p.year - 1 }))}
              disabled={!canPrev}
              style={{
                ...btn(),
                padding: "6px 14px",
                opacity: canPrev ? 1 : 0.25,
              }}
            >
              ‹
            </button>
            <button
              onClick={() => setPicker((p) => ({ ...p, mode: "year" }))}
              style={{
                ...btn(),
                padding: "6px 16px",
                fontWeight: "400",
                fontSize: "0.94rem",
                color: "#364734",
              }}
            >
              {picker.year} ▾
            </button>
            <button
              onClick={() => setPicker((p) => ({ ...p, year: p.year + 1 }))}
              disabled={!canNext}
              style={{
                ...btn(),
                padding: "6px 14px",
                opacity: canNext ? 1 : 0.25,
              }}
            >
              ›
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: "5px",
            }}
          >
            {MTHS.map((mn, i) => {
              const fut =
                picker.year > curYear ||
                (picker.year === curYear && i > curMonth);
              const active =
                calView.getFullYear() === picker.year &&
                calView.getMonth() === i;
              return (
                <button
                  key={mn}
                  disabled={fut}
                  onClick={() => {
                    setCalView(new Date(picker.year, i, 1));
                    setPicker(null);
                  }}
                  style={{
                    ...btn(),
                    padding: "9px 4px",
                    opacity: fut ? 0.28 : 1,
                    cursor: fut ? "default" : "pointer",
                    background: active ? acc : "rgba(255,255,255,0.35)",
                    border: active ? accB : "1px solid rgba(105,138,98,0.22)",
                    fontWeight: active ? "600" : "400",
                  }}
                >
                  {mn}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (picker.mode === "year") {
      const years = [];
      for (let yr = 2020; yr <= curYear; yr++) years.push(yr);
      return (
        <div style={{ padding: "4px 0" }} className="slide-down">
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "12px",
            }}
          >
            <button
              onClick={() => setPicker((p) => ({ ...p, mode: "month" }))}
              style={{ ...btn(), padding: "6px 14px", fontSize: "0.78rem" }}
            >
              ← Months
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: "5px",
            }}
          >
            {years.map((yr) => {
              const active = calView.getFullYear() === yr;
              return (
                <button
                  key={yr}
                  onClick={() =>
                    setPicker((p) => ({ ...p, mode: "month", year: yr }))
                  }
                  style={{
                    ...btn(),
                    padding: "10px 4px",
                    background: active ? acc : "rgba(255,255,255,0.35)",
                    border: active ? accB : "1px solid rgba(105,138,98,0.22)",
                    fontWeight: active ? "600" : "400",
                  }}
                >
                  {yr}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
  };

  // ── Calendar 1 ────────────────────────────────────────────────────────────
  const renderCalendar1 = () => {
    const today = new Date(),
      y = calView1.getFullYear(),
      m = calView1.getMonth();
    const days = new Date(y, m + 1, 0).getDate(),
      start = new Date(y, m, 1).getDay();
    const label = calView1.toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    });
    const DN = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    return (
      <div style={PANEL}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "18px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <button
              onClick={() => {
                const d = new Date(calView1);
                d.setMonth(m - 1);
                setCalView1(d);
              }}
              style={{
                background: "none",
                border: "none",
                color: TEXT,
                fontSize: "1.2rem",
                padding: "2px 6px",
                lineHeight: 1,
              }}
            >
              ‹
            </button>
            <button
              onClick={() =>
                setPicker1((p) => (p ? null : { mode: "month", year: y }))
              }
              style={{
                background: picker1 ? "rgba(139,154,127,0.15)" : "none",
                border: "none",
                color: "#364734",
                fontSize: "0.94rem",
                fontWeight: "400",
                minWidth: "132px",
                textAlign: "center",
                cursor: "pointer",
                padding: "3px 8px",
                borderRadius: "8px",
              }}
            >
              {label} {picker1 ? "▴" : "▾"}
            </button>
            <button
              onClick={() => {
                const d = new Date(calView1);
                d.setMonth(m + 1);
                setCalView1(d);
              }}
              style={{
                background: "none",
                border: "none",
                color: TEXT,
                fontSize: "1.2rem",
                padding: "2px 6px",
                lineHeight: 1,
              }}
            >
              ›
            </button>
          </div>
        </div>
        {picker1 ? (
          renderPicker(picker1, setPicker1, calView1, setCalView1)
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                marginBottom: "5px",
              }}
            >
              {DN.map((n) => (
                <div
                  key={n}
                  style={{
                    textAlign: "center",
                    fontSize: "0.62rem",
                    color: TEXT_MU,
                    fontWeight: "500",
                    letterSpacing: "0.1em",
                    paddingBottom: "5px",
                  }}
                >
                  {n}
                </div>
              ))}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                gap: "3px",
              }}
            >
              {Array.from({ length: start }, (_, i) => (
                <div key={"e" + i} />
              ))}
              {Array.from({ length: days }, (_, i) => {
                const day = i + 1,
                  dObj = new Date(y, m, day),
                  dk = toDK(dObj);
                const sgtStr = new Date().toLocaleDateString("en-CA", {
                  timeZone: "Asia/Singapore",
                });
                const [sy, sm, sd] = sgtStr.split("-").map(Number);
                const todaySGT = new Date(sy, sm - 1, sd);
                const isFuture = dObj > todaySGT;
                const isT =
                  today.getDate() === day &&
                  today.getMonth() === m &&
                  today.getFullYear() === y;
                const isS = selDate1 && toDK(selDate1) === dk;
                const hasBowel = bowelEntries.some(
                  (e) =>
                    toDK(new Date(e.timestamp)) === dk &&
                    (e.category === "bc_bowel" || e.category === "bowel")
                );
                const hasFood = bowelEntries.some(
                  (e) =>
                    toDK(new Date(e.timestamp)) === dk &&
                    (e.category === "bc_food" || e.category === "food")
                );
                const bowelColor =
                  bowelCats.find((c) => c.id === "bc_bowel")?.color ||
                  "#8b9a7f";
                const foodColor =
                  bowelCats.find((c) => c.id === "bc_food")?.color || "#c9a87c";
                return (
                  <div
                    key={day}
                    className={isFuture ? "" : "cal-cell"}
                    onClick={() => {
                      if (isFuture) return;
                      setSelDate1(dObj);
                      setFormOpen1(false);
                      setListFilter1(null);
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      aspectRatio: "1 / 1",
                      borderRadius: "50%",
                      cursor: isFuture ? "default" : "pointer",
                      opacity: isFuture ? 0.3 : 1,
                      background: isS
                        ? "linear-gradient(160deg,rgba(255,255,255,0.92),rgba(255,255,255,0.5))"
                        : isT
                        ? "rgba(111,146,102,0.2)"
                        : "transparent",
                      border: "none",
                      boxShadow: isS
                        ? "0 6px 14px -8px rgba(65,100,55,0.45),inset 0 0 0 1px rgba(255,255,255,0.75)"
                        : "none",
                      transform: isS ? "scale(1.06)" : "none",
                      transition:
                        "background 0.35s,box-shadow 0.35s,transform 0.3s cubic-bezier(.3,1.5,.5,1)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.8rem",
                        color: isS ? "#364734" : TEXT,
                        fontWeight: isS || isT ? "500" : "400",
                      }}
                    >
                      {day}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        gap: "3px",
                        marginTop: "3px",
                        height: "7px",
                        alignItems: "center",
                      }}
                    >
                      {hasBowel && (
                        <div
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            background: bowelColor,
                          }}
                        />
                      )}
                      {hasFood && (
                        <div
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            background: foodColor,
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "14px",
                marginTop: "12px",
              }}
            >
              {[
                ["rgba(150,185,140,0.45)", "Today"],
                ["rgba(139,154,127,0.8)", "Selected"],
              ].map(([col, lbl]) => (
                <div
                  key={lbl}
                  style={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  <div
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      background:
                        lbl === "Selected"
                          ? "linear-gradient(160deg,rgba(255,255,255,0.92),rgba(255,255,255,0.5))"
                          : "rgba(111,146,102,0.3)",
                      boxShadow:
                        lbl === "Selected"
                          ? "0 3px 6px -3px rgba(65,100,55,0.45),inset 0 0 0 1px rgba(255,255,255,0.8)"
                          : "none",
                    }}
                  />
                  <span style={{ fontSize: "0.65rem", color: TEXT_SM }}>
                    {lbl}
                  </span>
                </div>
              ))}
              {bowelCats.map((cat) => (
                <div
                  key={cat.id}
                  onClick={() => {
                    setListFilter1((f) => (f === cat.id ? null : cat.id));
                    setSelDate1(null);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: cat.color,
                      boxShadow:
                        listFilter1 === cat.id
                          ? `0 0 0 2px rgba(255,255,255,0.9),0 0 0 3.5px ${cat.color}`
                          : "none",
                      transition: "box-shadow 0.18s",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "0.65rem",
                      color: listFilter1 === cat.id ? cat.color : TEXT_SM,
                      fontWeight: listFilter1 === cat.id ? "600" : "400",
                      transition: "all 0.18s",
                    }}
                  >
                    {cat.name}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // ── Calendar 2 ────────────────────────────────────────────────────────────
  const renderCalendar2 = () => {
    const today = new Date(),
      y = calView2.getFullYear(),
      m = calView2.getMonth();
    const days = new Date(y, m + 1, 0).getDate(),
      start = new Date(y, m, 1).getDay();
    const label = calView2.toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    });
    const DN = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    return (
      <div style={PANEL}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "18px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <button
              onClick={() => {
                const d = new Date(calView2);
                d.setMonth(m - 1);
                setCalView2(d);
              }}
              style={{
                background: "none",
                border: "none",
                color: TEXT,
                fontSize: "1.2rem",
                padding: "2px 6px",
                lineHeight: 1,
              }}
            >
              ‹
            </button>
            <button
              onClick={() =>
                setPicker2((p) => (p ? null : { mode: "month", year: y }))
              }
              style={{
                background: picker2 ? "rgba(139,154,127,0.15)" : "none",
                border: "none",
                color: "#364734",
                fontSize: "0.94rem",
                fontWeight: "400",
                minWidth: "132px",
                textAlign: "center",
                cursor: "pointer",
                padding: "3px 8px",
                borderRadius: "8px",
              }}
            >
              {label} {picker2 ? "▴" : "▾"}
            </button>
            <button
              onClick={() => {
                const d = new Date(calView2);
                d.setMonth(m + 1);
                setCalView2(d);
              }}
              style={{
                background: "none",
                border: "none",
                color: TEXT,
                fontSize: "1.2rem",
                padding: "2px 6px",
                lineHeight: 1,
              }}
            >
              ›
            </button>
          </div>
        </div>
        {picker2 ? (
          renderPicker(picker2, setPicker2, calView2, setCalView2)
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                marginBottom: "5px",
              }}
            >
              {DN.map((n) => (
                <div
                  key={n}
                  style={{
                    textAlign: "center",
                    fontSize: "0.62rem",
                    color: TEXT_MU,
                    fontWeight: "500",
                    letterSpacing: "0.1em",
                    paddingBottom: "5px",
                  }}
                >
                  {n}
                </div>
              ))}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                gap: "3px",
              }}
            >
              {Array.from({ length: start }, (_, i) => (
                <div key={"e" + i} />
              ))}
              {Array.from({ length: days }, (_, i) => {
                const day = i + 1,
                  dObj = new Date(y, m, day),
                  dk = toDK(dObj);
                const sgtStr = new Date().toLocaleDateString("en-CA", {
                  timeZone: "Asia/Singapore",
                });
                const [sy, sm, sd] = sgtStr.split("-").map(Number);
                const todaySGT = new Date(sy, sm - 1, sd);
                const isFuture = dObj > todaySGT;
                const isT =
                  today.getDate() === day &&
                  today.getMonth() === m &&
                  today.getFullYear() === y;
                const isS = selDate2 && toDK(selDate2) === dk;
                const seen = new Set();
                const dots = symptomEntries
                  .filter((e) => toDK(new Date(e.timestamp)) === dk)
                  .reduce((acc, e) => {
                    if (!seen.has(e.categoryId)) {
                      seen.add(e.categoryId);
                      const c = symptomCats.find((x) => x.id === e.categoryId);
                      if (c) acc.push(c.color);
                    }
                    return acc;
                  }, []);
                return (
                  <div
                    key={day}
                    className={isFuture ? "" : "cal-cell"}
                    onClick={() => {
                      if (isFuture) return;
                      setSelDate2(dObj);
                      setFormOpen2(false);
                      setListFilter2(null);
                    }}
                    onTouchEnd={(e) => {
                      if (isFuture) return;
                      e.preventDefault();
                      setSelDate2(dObj);
                      setFormOpen2(false);
                      setListFilter2(null);
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      aspectRatio: "1 / 1",
                      borderRadius: "50%",
                      cursor: isFuture ? "default" : "pointer",
                      opacity: isFuture ? 0.3 : 1,
                      background: isS
                        ? "linear-gradient(160deg,rgba(255,255,255,0.92),rgba(255,255,255,0.5))"
                        : isT
                        ? "rgba(111,146,102,0.2)"
                        : "transparent",
                      border: "none",
                      boxShadow: isS
                        ? "0 6px 14px -8px rgba(65,100,55,0.45),inset 0 0 0 1px rgba(255,255,255,0.75)"
                        : "none",
                      transform: isS ? "scale(1.06)" : "none",
                      transition:
                        "background 0.35s,box-shadow 0.35s,transform 0.3s cubic-bezier(.3,1.5,.5,1)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.8rem",
                        color: isS ? "#364734" : TEXT,
                        fontWeight: isS || isT ? "500" : "400",
                      }}
                    >
                      {day}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        gap: "2px",
                        marginTop: "2px",
                        minHeight: "7px",
                        maxWidth: "21px",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {dots.slice(0, 6).map((col, xi) => (
                        <div
                          key={xi}
                          style={{
                            width: "5px",
                            height: "5px",
                            borderRadius: "50%",
                            background: col,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                marginTop: "12px",
              }}
            >
              {[
                ["rgba(150,185,140,0.45)", "Today"],
                ["rgba(139,154,127,0.8)", "Selected"],
              ].map(([col, lbl]) => (
                <div
                  key={lbl}
                  style={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  <div
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      background:
                        lbl === "Selected"
                          ? "linear-gradient(160deg,rgba(255,255,255,0.92),rgba(255,255,255,0.5))"
                          : "rgba(111,146,102,0.3)",
                      boxShadow:
                        lbl === "Selected"
                          ? "0 3px 6px -3px rgba(65,100,55,0.45),inset 0 0 0 1px rgba(255,255,255,0.8)"
                          : "none",
                    }}
                  />
                  <span style={{ fontSize: "0.65rem", color: TEXT_SM }}>
                    {lbl}
                  </span>
                </div>
              ))}
              {symptomCats.map((cat) => (
                <div
                  key={cat.id}
                  onClick={() => {
                    setListFilter2((f) => (f === cat.id ? null : cat.id));
                    setSelDate2(null);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: cat.color,
                      boxShadow:
                        listFilter2 === cat.id
                          ? `0 0 0 2px rgba(255,255,255,0.9),0 0 0 3.5px ${cat.color}`
                          : "none",
                      transition: "box-shadow 0.18s",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "0.64rem",
                      color: listFilter2 === cat.id ? cat.color : TEXT_SM,
                      fontWeight: listFilter2 === cat.id ? "600" : "400",
                      transition: "all 0.18s",
                    }}
                  >
                    {cat.name}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // ── Tab 1 ──────────────────────────────────────────────────────────────────
  // ── All-entries list (category filter view) ──────────────────────────────
  const renderEntryList = (entries, color, title, onClose, onEdit, onDel) => {
    if (!entries.length)
      return (
        <div style={PANEL} className="slide-down">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: color,
                  boxShadow: `0 0 0 2px rgba(255,255,255,0.9),0 0 0 3.5px ${color}`,
                }}
              />
              <span
                style={{ fontSize: "0.94rem", fontWeight: "400", color: "#364734" }}
              >
                {title}
              </span>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                color: TEXT_SM,
                fontSize: "0.9rem",
                padding: "2px 5px",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
          <div
            style={{
              textAlign: "center",
              padding: "12px 0",
              color: TEXT_MU,
              fontSize: "0.8rem",
              fontStyle: "italic",
            }}
          >
            No entries yet
          </div>
        </div>
      );
    // Group by date, most recent first
    const groups = {};
    entries.forEach((e) => {
      const dk = toDK(new Date(e.timestamp));
      if (!groups[dk]) groups[dk] = [];
      groups[dk].push(e);
    });
    const dates = Object.keys(groups).sort().reverse();
    return (
      <div style={PANEL} className="slide-down">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "14px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: color,
                boxShadow: `0 0 0 2px rgba(255,255,255,0.9),0 0 0 3.5px ${color}`,
              }}
            />
            <span
              style={{ fontSize: "0.94rem", fontWeight: "400", color: "#364734" }}
            >
              {title}
            </span>
            <span
              style={{
                fontSize: "0.68rem",
                color: TEXT_SM,
                background: "rgba(105,138,98,0.16)",
                padding: "1px 6px",
                borderRadius: "8px",
              }}
            >
              {entries.length}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: TEXT_SM,
              fontSize: "0.9rem",
              padding: "2px 5px",
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>
        {dates.map((dk) => (
          <div key={dk} style={{ marginBottom: "14px" }}>
            <div
              style={{
                fontSize: "0.72rem",
                fontWeight: "600",
                color: TEXT_SM,
                marginBottom: "6px",
              }}
            >
              {fmtD(new Date(dk + "T12:00"))}
            </div>
            {groups[dk]
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
              .map((e) =>
                renderEntryCard(
                  e,
                  color,
                  () => {
                    onDel(e.id);
                  },
                  e.severity ? (
                    <span
                      style={{
                        display: "inline-flex",
                        gap: "2px",
                        alignItems: "center",
                        marginLeft: "2px",
                      }}
                    >
                      {[1, 2, 3, 4, 5].map((n) => (
                        <span
                          key={n}
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            display: "inline-block",
                            background:
                              n <= e.severity
                                ? color
                                : "rgba(105,138,98,0.28)",
                          }}
                        />
                      ))}
                    </span>
                  ) : null,
                  () => {
                    onClose();
                    onEdit(e);
                  }
                )
              )}
          </div>
        ))}
      </div>
    );
  };

  const renderTab1 = () => {
    const selKey = selDate1 ? toDK(selDate1) : null;
    const dayArr = selKey
      ? bowelEntries.filter((e) => toDK(new Date(e.timestamp)) === selKey)
      : [];
    const activeCat1 = bowelCats.find((c) => c.id === formOpen1);
    const curColor = activeCat1?.color || "#8b9a7f";
    const curTags = activeCat1?.tags || [];

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {renderCalendar1()}

        {listFilter1 &&
          (() => {
            const cat = bowelCats.find((c) => c.id === listFilter1);
            return renderEntryList(
              bowelEntries.filter((e) => e.category === listFilter1),
              cat?.color || "#8b9a7f",
              `All ${cat?.name || ""} Entries`,
              () => setListFilter1(null),
              (entry) => {
                setListFilter1(null);
                setSelDate1(new Date(entry.timestamp));
                setTimeout(() => openEditForm1(entry), 50);
              },
              delEntry1
            );
          })()}

        {/* Undo delete toast */}
        {undoEntry1 && (
          <div
            style={{
              ...PANEL,
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "rgba(58,84,56,0.55)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
            className="slide-down"
          >
            <span
              style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.88)" }}
            >
              Entry deleted
            </span>
            <button
              onClick={undoDelete1}
              style={{
                background: "rgba(255,255,255,0.18)",
                border: "1px solid rgba(255,255,255,0.35)",
                borderRadius: "8px",
                color: "rgba(255,255,255,0.95)",
                fontSize: "0.78rem",
                fontWeight: "600",
                padding: "4px 12px",
                cursor: "pointer",
              }}
            >
              Undo
            </button>
          </div>
        )}
        {!listFilter1 && selDate1 && (
          <>
          <div style={PANEL} className="slide-down">
            <div style={{ marginBottom: "14px" }}>
              <div
                style={{ fontSize: "0.94rem", fontWeight: "400", color: "#364734" }}
              >
                {fmtD(selDate1)}
              </div>
              <div
                style={{
                  fontSize: "0.71rem",
                  color: TEXT_SM,
                  marginTop: "2px",
                }}
              >
                {dayArr.length} {dayArr.length === 1 ? "entry" : "entries"}{" "}
                recorded
              </div>
            </div>
            {dayArr.length === 0 && !formOpen1 && null}
            {bowelCats.map((cat) => {
              const catArr = dayArr
                .filter((e) => e.category === cat.id)
                .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
              if (!catArr.length) return null;
              return (
                <div key={cat.id} style={{ marginBottom: "6px" }}>
                  {renderSectionHead(cat.color, cat.name, catArr.length)}
                  {catArr.map((e) =>
                    renderEntryCard(
                      e,
                      cat.color,
                      () => delEntry1(e.id),
                      e.severity ? (
                        <span
                          style={{
                            display: "inline-flex",
                            gap: "2px",
                            alignItems: "center",
                            marginLeft: "2px",
                          }}
                        >
                          {[1, 2, 3, 4, 5].map((n) => (
                            <span
                              key={n}
                              style={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                display: "inline-block",
                                background:
                                  n <= e.severity
                                    ? cat.color
                                    : "rgba(105,138,98,0.28)",
                              }}
                            />
                          ))}
                        </span>
                      ) : null,
                      () => openEditForm1(e)
                    )
                  )}
                </div>
              );
            })}
          </div>
          <div style={PANEL} className="slide-down">
            {!formOpen1 && (
              <div
                style={{
                  fontSize: "0.94rem",
                  fontWeight: "400",
                  color: "#364734",
                  marginBottom: "12px",
                }}
              >
                New entry
              </div>
            )}
            {!formOpen1 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "7px",
                  marginBottom: "0",
                }}
              >
                {bowelCats.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => openForm1(cat.id)}
                    style={{
                      padding: "8px 13px",
                      borderRadius: "11px",
                      fontSize: "0.78rem",
                      fontWeight: "500",
                      background: cat.color + "18",
                      border: `1.5px solid ${cat.color}38`,
                      color: cat.color,
                    }}
                  >
                    + {cat.name}
                  </button>
                ))}
              </div>
            )}
            {formOpen1 && (
              <div
                style={{
                  ...INNER,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  marginBottom: "0",
                }}
                className="slide-down"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "13px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.94rem",
                        fontWeight: "400",
                        color: "#364734",
                      }}
                    >
                      New Entry
                    </span>
                    {activeCat1 && (
                      <span
                        style={{
                          padding: "2px 9px",
                          borderRadius: "10px",
                          background: curColor + "20",
                          border: `1px solid ${curColor}38`,
                          color: curColor,
                          fontSize: "0.76rem",
                          fontWeight: "500",
                        }}
                      >
                        {activeCat1.name}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={closeForm1}
                    style={{
                      background: "none",
                      border: "none",
                      color: TEXT_SM,
                      fontSize: "0.9rem",
                      padding: "2px 5px",
                    }}
                  >
                    ✕
                  </button>
                </div>
                {activeCat1?.id === "bc_food" && !f1Legacy ? (
                  <div style={{ marginBottom: "12px" }}>
                    <FLabel>Food</FLabel>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {MEAL_FIELDS.map((m) => (
                        <div
                          key={m.id}
                          style={{ ...INNER, padding: "10px 12px" }}
                        >
                          <div
                            style={{
                              fontSize: "0.78rem",
                              color: TEXT_SM,
                              marginBottom: "6px",
                            }}
                          >
                            {m.label}
                          </div>
                          <textarea
                            value={f1Meals[m.id]}
                            onChange={(e) =>
                              setF1Meals((p) => ({
                                ...p,
                                [m.id]: e.target.value,
                              }))
                            }
                            rows={2}
                            placeholder=""
                            style={{ ...INPUT }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ marginBottom: "12px" }}>
                    <FLabel>
                      {activeCat1?.id === "bc_food" ? "Food" : "Description"}
                    </FLabel>
                    <textarea
                      value={f1Desc}
                      onChange={(e) => setF1Desc(e.target.value)}
                      rows={4}
                      placeholder=""
                      style={{ ...INPUT }}
                    />
                  </div>
                )}
                {activeCat1?.id === "bc_food" && (
                  <div style={{ marginBottom: "12px" }}>
                    <FLabel>Snacks</FLabel>
                    <textarea
                      value={f1Meals.snacks}
                      onChange={(e) =>
                        setF1Meals((p) => ({ ...p, snacks: e.target.value }))
                      }
                      rows={2}
                      placeholder=""
                      style={{ ...INPUT }}
                    />
                  </div>
                )}
                <div style={{ marginBottom: "12px" }}>
                  <FLabel>
                    {activeCat1?.id === "bc_food" ? "Drinks" : "Additional"}
                  </FLabel>
                  <textarea
                    value={f1Additional}
                    onChange={(e) => setF1Additional(e.target.value)}
                    rows={2}
                    placeholder=""
                    style={{ ...INPUT }}
                  />
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <FLabel>Tags</FLabel>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      marginBottom: "8px",
                    }}
                  >
                    {curTags.map((tag) =>
                      renderPill(tag, f1Tags.includes(tag), curColor, () =>
                        toggleF1Tag(tag)
                      )
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <input
                      value={f1NewTag}
                      onChange={(e) => setF1NewTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") addF1Tag();
                      }}
                      placeholder="Add custom tag (saved for future)…"
                      style={{ ...INPUT, flex: 1, padding: "7px 11px" }}
                    />
                    <button
                      onClick={addF1Tag}
                      style={{
                        padding: "7px 13px",
                        borderRadius: "10px",
                        background: curColor + "1a",
                        border: `1px solid ${curColor}32`,
                        color: curColor,
                        fontSize: "0.82rem",
                      }}
                    >
                      +
                    </button>
                  </div>
                  {f1Tags.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "5px",
                        marginTop: "8px",
                        paddingTop: "8px",
                        borderTop: `1px dashed ${curColor}1e`,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.67rem",
                          color: TEXT_SM,
                          alignSelf: "center",
                        }}
                      >
                        Selected:
                      </span>
                      {f1Tags.map((t) =>
                        renderPill(t, true, curColor, () => toggleF1Tag(t))
                      )}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <FLabel>
                    {activeCat1?.protected ? "Amount" : "Severity"}
                  </FLabel>
                  {renderSevPicker(f1Sev, setF1Sev, curColor)}
                </div>
                <div style={{ marginBottom: "14px" }}>
                  <FLabel>Date &amp; Time of Episode</FLabel>
                  <div style={{ overflow: "hidden", borderRadius: "11px" }}>
                    <input
                      type="datetime-local"
                      value={f1Time}
                      onChange={(e) => setF1Time(e.target.value)}
                      style={{ ...INPUT }}
                    />
                  </div>
                </div>
                <button
                  onClick={saveEntry1}
                  style={{
                    width: "100%",
                    padding: "11px",
                    borderRadius: "11px",
                    fontSize: "0.82rem",
                    fontWeight: "600",
                    letterSpacing: "0.03em",
                    background: curColor + "1e",
                    border: `1.5px solid ${curColor}40`,
                    color: curColor,
                  }}
                >
                  {editingEntryId1 ? "Update Entry" : "Save Entry"}
                </button>
              </div>
            )}
          </div>
          </>
        )}

        {/* Manage Categories */}
        <div style={PANEL}>
          <button
            onClick={() => setShowCatMgr1((p) => !p)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              background: "none",
              border: "none",
              padding: 0,
              color: "#364734",
              fontSize: "0.94rem",
              fontWeight: "400",
            }}
          >
            <span>Manage Categories</span>
            <span
              style={{
                fontSize: "0.68rem",
                color: TEXT_SM,
                transform: showCatMgr1 ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
                display: "inline-block",
              }}
            >
              ▾
            </span>
          </button>
          {showCatMgr1 && (
            <div style={{ marginTop: "16px" }} className="slide-down">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  marginBottom: "16px",
                }}
              >
                {bowelCats.map((cat) => (
                  <div
                    key={cat.id}
                    style={{
                      borderRadius: "13px",
                      background: cat.color + "0c",
                      border: `1px solid ${cat.color}20`,
                      overflow: "hidden",
                    }}
                  >
                    {editingBowelCat?.id === cat.id ? (
                      <div
                        style={{ padding: "10px 13px" }}
                        className="slide-down"
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            alignItems: "center",
                            marginBottom: "8px",
                          }}
                        >
                          <input
                            value={editingBowelCat.name}
                            onChange={(e) =>
                              setEditingBowelCat((p) => ({
                                ...p,
                                name: e.target.value,
                              }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEditBowelCat();
                              if (e.key === "Escape") setEditingBowelCat(null);
                            }}
                            autoFocus
                            style={{
                              ...INPUT,
                              flex: 1,
                              padding: "6px 10px",
                              fontSize: "0.82rem",
                            }}
                          />
                          <button
                            type="button"
                            onClick={saveEditBowelCat}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#8b9a7f",
                              fontSize: "0.88rem",
                              padding: "3px 6px",
                              cursor: "pointer",
                              fontWeight: "600",
                            }}
                          >
                            ✓
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingBowelCat(null)}
                            style={{
                              background: "none",
                              border: "none",
                              color: TEXT_MU,
                              fontSize: "0.88rem",
                              padding: "3px 5px",
                              cursor: "pointer",
                            }}
                          >
                            ✕
                          </button>
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(7,1fr)",
                            gap: "5px",
                            maxWidth: "200px",
                          }}
                        >
                          {CAT_COLORS.map((c) => (
                            <div
                              key={c}
                              onClick={() =>
                                setEditingBowelCat((p) => ({ ...p, color: c }))
                              }
                              style={{
                                width: "22px",
                                height: "22px",
                                borderRadius: "50%",
                                background: c,
                                cursor: "pointer",
                                border:
                                  editingBowelCat.color === c
                                    ? "2.5px solid rgba(70,62,50,0.62)"
                                    : "1.5px solid rgba(105,138,98,0.24)",
                                boxShadow:
                                  editingBowelCat.color === c
                                    ? `0 0 6px ${c}80`
                                    : "none",
                                transition: "all 0.12s",
                              }}
                            />
                          ))}
                          <ColorWheel
                            value={editingBowelCat.color}
                            onChange={(c) => setEditingBowelCat((p) => ({ ...p, color: c }))}
                          />
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "9px 13px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              background: cat.color,
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              color: TEXT,
                              fontSize: "0.82rem",
                              fontWeight: "500",
                            }}
                          >
                            {cat.name}
                          </span>
                          <span style={{ color: TEXT_SM, fontSize: "0.67rem" }}>
                            {cat.tags.length} tag
                            {cat.tags.length !== 1 ? "s" : ""}
                          </span>
                          {cat.protected && (
                            <span
                              style={{
                                fontSize: "0.6rem",
                                color: TEXT_MU,
                                background: "rgba(105,138,98,0.18)",
                                padding: "1px 5px",
                                borderRadius: "6px",
                              }}
                            >
                              default
                            </span>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: "3px" }}>
                          <button
                            type="button"
                            onClick={() =>
                              setEditingBowelCat({
                                id: cat.id,
                                name: cat.name,
                                color: cat.color,
                              })
                            }
                            style={{
                              background: "none",
                              border: "none",
                              color: cat.color + "aa",
                              fontSize: "0.75rem",
                              padding: "2px 6px",
                              borderRadius: "6px",
                              cursor: "pointer",
                            }}
                          >
                            ✎
                          </button>
                          <button
                            onClick={() =>
                              setExpandBowelCatId(
                                expandBowelCatId === cat.id ? null : cat.id
                              )
                            }
                            style={{
                              background: "none",
                              border: "none",
                              color: cat.color,
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              borderRadius: "6px",
                            }}
                          >
                            {expandBowelCatId === cat.id ? "▲" : "▼"} tags
                          </button>
                          {!cat.protected && (
                            <button
                              type="button"
                              onClick={() =>
                                saveBC(bowelCats.filter((c) => c.id !== cat.id))
                              }
                              style={{
                                background: "none",
                                border: "none",
                                color: "rgba(160,144,126,0.36)",
                                fontSize: "0.84rem",
                                padding: "2px 6px",
                              }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    {expandBowelCatId === cat.id && (
                      <div
                        style={{ padding: "0 13px 12px" }}
                        className="slide-down"
                      >
                        <div style={{ marginBottom: "7px" }}>
                          {cat.tags.map((t, i) =>
                            renderTagRow(
                              t,
                              i,
                              cat.tags.length,
                              cat.id,
                              cat.color,
                              () => removeTagFromBowelCat(cat.id, i)
                            )
                          )}
                        </div>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <input
                            value={bowelCatTagInputs[cat.id] || ""}
                            onChange={(e) =>
                              setBowelCatTagInputs((p) => ({
                                ...p,
                                [cat.id]: e.target.value,
                              }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") addTagToBowelCat(cat.id);
                            }}
                            placeholder="New tag…"
                            style={{ ...INPUT, flex: 1, padding: "6px 10px" }}
                          />
                          <button
                            onClick={() => addTagToBowelCat(cat.id)}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "9px",
                              background: cat.color + "16",
                              border: `1px solid ${cat.color}30`,
                              color: cat.color,
                              fontSize: "0.82rem",
                            }}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div
                style={{
                  borderTop: "1px solid rgba(105,138,98,0.14)",
                  paddingTop: "14px",
                }}
              >
                <FLabel>Add New Category</FLabel>
                <div
                  style={{
                    display: "flex",
                    gap: "7px",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <input
                    value={newBowelCat.name}
                    onChange={(e) =>
                      setNewBowelCat((p) => ({ ...p, name: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addBowelCat();
                    }}
                    placeholder="Category name…"
                    style={{ ...INPUT, flex: 1, padding: "8px 11px" }}
                  />
                  <button
                    onClick={addBowelCat}
                    style={{
                      padding: "8px 14px",
                      borderRadius: "10px",
                      background: "rgba(139,154,127,0.18)",
                      border: "1px solid rgba(139,154,127,0.3)",
                      color: "#556650",
                      fontSize: "0.82rem",
                      flexShrink: 0,
                    }}
                  >
                    +
                  </button>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7,1fr)",
                    gap: "6px",
                    maxWidth: "200px",
                  }}
                >
                  {CAT_COLORS.map((c) => (
                    <div
                      key={c}
                      onClick={() =>
                        setNewBowelCat((p) => ({ ...p, color: c }))
                      }
                      style={{
                        width: "22px",
                        height: "22px",
                        borderRadius: "50%",
                        background: c,
                        cursor: "pointer",
                        border:
                          newBowelCat.color === c
                            ? "2.5px solid rgba(70,62,50,0.6)"
                            : "1.5px solid rgba(105,138,98,0.24)",
                        boxShadow:
                          newBowelCat.color === c ? `0 0 6px ${c}80` : "none",
                        transition: "all 0.12s",
                      }}
                    />
                  ))}
                  <ColorWheel
                    value={newBowelCat.color}
                    onChange={(c) => setNewBowelCat((p) => ({ ...p, color: c }))}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Tab 2 ──────────────────────────────────────────────────────────────────
  const renderTab2 = () => {
    const selKey = selDate2 ? toDK(selDate2) : null;
    const dayArr = selKey
      ? symptomEntries
          .filter((e) => toDK(new Date(e.timestamp)) === selKey)
          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      : [];
    const actCat = symptomCats.find((c) => c.id === f2CatId) || symptomCats[0];

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        {renderCalendar2()}

        {/* List view when a legend dot is active */}
        {listFilter2 &&
          (() => {
            const cat = symptomCats.find((c) => c.id === listFilter2);
            return renderEntryList(
              symptomEntries.filter((e) => e.categoryId === listFilter2),
              cat?.color || "#9a9080",
              `All ${cat?.name || ""} Entries`,
              () => setListFilter2(null),
              (entry) => {
                setListFilter2(null);
                setSelDate2(new Date(entry.timestamp));
                setTimeout(() => openEditForm2(entry), 50);
              },
              delEntry2
            );
          })()}

        {/* Undo delete toast */}
        {undoEntry2 && (
          <div
            style={{
              ...PANEL,
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "rgba(58,84,56,0.55)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
            className="slide-down"
          >
            <span
              style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.88)" }}
            >
              Entry deleted
            </span>
            <button
              onClick={undoDelete2}
              style={{
                background: "rgba(255,255,255,0.18)",
                border: "1px solid rgba(255,255,255,0.35)",
                borderRadius: "8px",
                color: "rgba(255,255,255,0.95)",
                fontSize: "0.78rem",
                fontWeight: "600",
                padding: "4px 12px",
                cursor: "pointer",
              }}
            >
              Undo
            </button>
          </div>
        )}
        {!listFilter2 && selDate2 && (
          <>
          <div ref={datePanelRef2} style={PANEL} className="slide-down">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "14px",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "0.94rem",
                    fontWeight: "400",
                    color: "#364734",
                  }}
                >
                  {fmtD(selDate2)}
                </div>
                <div
                  style={{
                    fontSize: "0.71rem",
                    color: TEXT_SM,
                    marginTop: "2px",
                  }}
                >
                  {dayArr.length} {dayArr.length === 1 ? "entry" : "entries"}{" "}
                  recorded
                </div>
              </div>
            </div>

            {dayArr.length === 0 && !formOpen2 && null}
            {/* Group by category — same structure as Tab 1 */}
            {symptomCats.map((cat) => {
              const catEntries = dayArr.filter((e) => e.categoryId === cat.id);
              if (catEntries.length === 0) return null;
              return (
                <div key={cat.id} style={{ marginBottom: "6px" }}>
                  {renderSectionHead(cat.color, cat.name, catEntries.length)}
                  {catEntries.map((e) =>
                    renderEntryCard(
                      e,
                      cat.color,
                      () => delEntry2(e.id),
                      e.severity ? (
                        <span
                          style={{
                            display: "inline-flex",
                            gap: "2px",
                            alignItems: "center",
                            marginLeft: "2px",
                          }}
                        >
                          {[1, 2, 3, 4, 5].map((n) => (
                            <span
                              key={n}
                              style={{
                                width: "6px",
                                height: "6px",
                                borderRadius: "50%",
                                display: "inline-block",
                                background:
                                  n <= e.severity
                                    ? cat.color
                                    : "rgba(105,138,98,0.28)",
                              }}
                            />
                          ))}
                        </span>
                      ) : null,
                      () => openEditForm2(e)
                    )
                  )}
                </div>
              );
            })}
          </div>
          <div style={PANEL} className="slide-down">
            {/* Per-category log buttons — shown when no form is open */}
            {!formOpen2 && (
              <div
                style={{
                  fontSize: "0.94rem",
                  fontWeight: "400",
                  color: "#364734",
                  marginBottom: "12px",
                }}
              >
                New entry
              </div>
            )}
            {!formOpen2 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "7px",
                  marginBottom: "0",
                }}
              >
                {symptomCats.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => openForm2(cat.id)}
                    style={{
                      padding: "8px 13px",
                      borderRadius: "11px",
                      fontSize: "0.78rem",
                      fontWeight: "500",
                      background: cat.color + "18",
                      border: `1.5px solid ${cat.color}38`,
                      color: cat.color,
                    }}
                  >
                    + {cat.name}
                  </button>
                ))}
              </div>
            )}

            {formOpen2 && (
              <div
                style={{
                  ...INNER,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  marginBottom: "0",
                }}
                className="slide-down"
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "13px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.94rem",
                        fontWeight: "400",
                        color: "#364734",
                      }}
                    >
                      New Entry
                    </span>
                    {actCat && (
                      <span
                        style={{
                          padding: "2px 9px",
                          borderRadius: "10px",
                          background: actCat.color + "20",
                          border: `1px solid ${actCat.color}38`,
                          color: actCat.color,
                          fontSize: "0.76rem",
                          fontWeight: "500",
                        }}
                      >
                        {actCat.name}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={closeForm2}
                    style={{
                      background: "none",
                      border: "none",
                      color: TEXT_SM,
                      fontSize: "0.9rem",
                      padding: "2px 5px",
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div style={{ marginBottom: "11px" }}>
                  <FLabel>Description</FLabel>
                  <textarea
                    value={f2Desc}
                    onChange={(e) => setF2Desc(e.target.value)}
                    rows={4}
                    placeholder=""
                    style={{ ...INPUT }}
                  />
                </div>
                <div style={{ marginBottom: "11px" }}>
                  <FLabel>
                    <em
                      style={{ fontStyle: "italic", letterSpacing: "0.04em" }}
                    >
                      Treatment
                    </em>
                  </FLabel>
                  <textarea
                    value={f2Treatment}
                    onChange={(e) => setF2Treatment(e.target.value)}
                    rows={2}
                    placeholder=""
                    style={{ ...INPUT, fontStyle: "italic" }}
                  />
                </div>
                <div style={{ marginBottom: "11px" }}>
                  <FLabel>Tags</FLabel>
                  {actCat?.tags?.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        marginBottom: "8px",
                      }}
                    >
                      {actCat.tags.map((tag) =>
                        renderPill(
                          tag,
                          f2Tags.includes(tag),
                          actCat.color,
                          () => toggleF2Tag(tag)
                        )
                      )}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: "6px" }}>
                    <input
                      value={f2NewTag}
                      onChange={(e) => setF2NewTag(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") addF2Tag();
                      }}
                      placeholder="Add custom tag…"
                      style={{ ...INPUT, flex: 1, padding: "7px 11px" }}
                    />
                    <button
                      onClick={addF2Tag}
                      style={{
                        padding: "7px 13px",
                        borderRadius: "10px",
                        background: (actCat?.color || "#9a9080") + "1a",
                        border: `1px solid ${actCat?.color || "#9a9080"}32`,
                        color: actCat?.color || "#9a9080",
                        fontSize: "0.82rem",
                      }}
                    >
                      +
                    </button>
                  </div>
                  {f2Tags.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "5px",
                        marginTop: "8px",
                        paddingTop: "8px",
                        borderTop: `1px dashed ${actCat?.color || "#9a9080"}1e`,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.67rem",
                          color: TEXT_SM,
                          alignSelf: "center",
                        }}
                      >
                        Selected:
                      </span>
                      {f2Tags.map((t) =>
                        renderPill(t, true, actCat?.color || "#9a9080", () =>
                          toggleF2Tag(t)
                        )
                      )}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: "11px" }}>
                  <FLabel>Severity</FLabel>
                  {renderSevPicker(f2Sev, setF2Sev, actCat?.color)}
                </div>
                {/* All Day / Time toggle */}
                <div style={{ marginBottom: "14px" }}>
                  <FLabel>Timing</FLabel>
                  <div
                    style={{
                      display: "flex",
                      gap: "7px",
                      marginBottom: f2AllDay ? 0 : "8px",
                    }}
                  >
                    {[
                      ["allday", true, "All Day"],
                      ["time", false, "Specific Time"],
                    ].map(([id, val, lbl]) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => {
                          setF2AllDay(val);
                          if (!val && !f2Time) setF2Time(nowLocal(selDate2));
                        }}
                        style={{
                          flex: 1,
                          padding: "7px 8px",
                          borderRadius: "9px",
                          fontSize: "0.76rem",
                          fontWeight: "500",
                          background:
                            f2AllDay === val
                              ? (actCat?.color || "#9a9080") + "22"
                              : "rgba(255,255,255,0.5)",
                          border:
                            f2AllDay === val
                              ? `1.5px solid ${actCat?.color || "#9a9080"}45`
                              : "1px solid rgba(105,138,98,0.26)",
                          color:
                            f2AllDay === val
                              ? actCat?.color || "#9a9080"
                              : TEXT_SM,
                        }}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                  {!f2AllDay && (
                    <div
                      style={{ overflow: "hidden", borderRadius: "11px" }}
                      className="slide-down"
                    >
                      <input
                        type="datetime-local"
                        value={f2Time}
                        onChange={(e) => setF2Time(e.target.value)}
                        style={{ ...INPUT }}
                      />
                    </div>
                  )}
                </div>
                <button
                  onClick={saveEntry2}
                  style={{
                    width: "100%",
                    padding: "11px",
                    borderRadius: "11px",
                    fontSize: "0.82rem",
                    fontWeight: "600",
                    letterSpacing: "0.03em",
                    background: (actCat?.color || "#9a9080") + "1c",
                    border: `1.5px solid ${actCat?.color || "#9a9080"}3e`,
                    color: actCat?.color || "#5a5048",
                  }}
                >
                  {editingEntryId2 ? "Update Entry" : "Save Entry"}
                </button>
              </div>
            )}
          </div>
          </>
        )}

        {/* Manage Categories */}
        <div style={PANEL}>
          <button
            onClick={() => setShowCatMgr((p) => !p)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              background: "none",
              border: "none",
              padding: 0,
              color: "#364734",
              fontSize: "0.94rem",
              fontWeight: "400",
            }}
          >
            <span>Manage Categories</span>
            <span
              style={{
                fontSize: "0.68rem",
                color: TEXT_SM,
                transform: showCatMgr ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
                display: "inline-block",
              }}
            >
              ▾
            </span>
          </button>
          {showCatMgr && (
            <div style={{ marginTop: "16px" }} className="slide-down">
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  marginBottom: "16px",
                }}
              >
                {symptomCats.map((cat) => (
                  <div
                    key={cat.id}
                    style={{
                      borderRadius: "13px",
                      background: cat.color + "0c",
                      border: `1px solid ${cat.color}20`,
                      overflow: "hidden",
                    }}
                  >
                    {/* ── Edit mode: name input + colour swatches ── */}
                    {editingCat?.id === cat.id ? (
                      <div
                        style={{ padding: "10px 13px" }}
                        className="slide-down"
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            alignItems: "center",
                            marginBottom: "8px",
                          }}
                        >
                          <input
                            value={editingCat.name}
                            onChange={(e) =>
                              setEditingCat((p) => ({
                                ...p,
                                name: e.target.value,
                              }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEditCat();
                              if (e.key === "Escape") setEditingCat(null);
                            }}
                            autoFocus
                            style={{
                              ...INPUT,
                              flex: 1,
                              padding: "6px 10px",
                              fontSize: "0.82rem",
                            }}
                          />
                          <button
                            type="button"
                            onClick={saveEditCat}
                            style={{
                              background: "none",
                              border: "none",
                              color: "#8b9a7f",
                              fontSize: "0.88rem",
                              padding: "3px 6px",
                              cursor: "pointer",
                              fontWeight: "600",
                            }}
                          >
                            ✓
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingCat(null)}
                            style={{
                              background: "none",
                              border: "none",
                              color: TEXT_MU,
                              fontSize: "0.88rem",
                              padding: "3px 5px",
                              cursor: "pointer",
                            }}
                          >
                            ✕
                          </button>
                        </div>
                        {/* Colour swatches */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(7,1fr)",
                            gap: "5px",
                            maxWidth: "200px",
                          }}
                        >
                          {CAT_COLORS.map((c) => (
                            <div
                              key={c}
                              onClick={() =>
                                setEditingCat((p) => ({ ...p, color: c }))
                              }
                              style={{
                                width: "22px",
                                height: "22px",
                                borderRadius: "50%",
                                background: c,
                                cursor: "pointer",
                                border:
                                  editingCat.color === c
                                    ? "2.5px solid rgba(70,62,50,0.62)"
                                    : "1.5px solid rgba(105,138,98,0.24)",
                                boxShadow:
                                  editingCat.color === c
                                    ? `0 0 6px ${c}80`
                                    : "none",
                                transition: "all 0.12s",
                              }}
                            />
                          ))}
                          <ColorWheel
                            value={editingCat.color}
                            onChange={(c) => setEditingCat((p) => ({ ...p, color: c }))}
                          />
                        </div>
                      </div>
                    ) : (
                      /* ── View mode ── */
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "9px 13px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <div
                            style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              background: cat.color,
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              color: TEXT,
                              fontSize: "0.82rem",
                              fontWeight: "500",
                            }}
                          >
                            {cat.name}
                          </span>
                          <span style={{ color: TEXT_SM, fontSize: "0.67rem" }}>
                            {cat.tags.length} tag
                            {cat.tags.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: "3px" }}>
                          <button
                            type="button"
                            onClick={() =>
                              setEditingCat({
                                id: cat.id,
                                name: cat.name,
                                color: cat.color,
                              })
                            }
                            style={{
                              background: "none",
                              border: "none",
                              color: cat.color + "aa",
                              fontSize: "0.75rem",
                              padding: "2px 6px",
                              borderRadius: "6px",
                              cursor: "pointer",
                            }}
                          >
                            ✎
                          </button>
                          <button
                            onClick={() =>
                              setExpandCatId(
                                expandCatId === cat.id ? null : cat.id
                              )
                            }
                            style={{
                              background: "none",
                              border: "none",
                              color: cat.color,
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              borderRadius: "6px",
                            }}
                          >
                            {expandCatId === cat.id ? "▲" : "▼"} tags
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteCat(cat.id)}
                            style={{
                              background: "none",
                              border: "none",
                              color: "rgba(160,144,126,0.36)",
                              fontSize: "0.84rem",
                              padding: "2px 6px",
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    )}
                    {expandCatId === cat.id && (
                      <div
                        style={{ padding: "0 13px 12px" }}
                        className="slide-down"
                      >
                        <div style={{ marginBottom: "7px" }}>
                          {cat.tags.map((t, i) =>
                            renderTagRow(
                              t,
                              i,
                              cat.tags.length,
                              cat.id,
                              cat.color,
                              () => removeTagFromCat(cat.id, i)
                            )
                          )}
                        </div>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <input
                            value={catTagInputs[cat.id] || ""}
                            onChange={(e) =>
                              setCatTagInputs((p) => ({
                                ...p,
                                [cat.id]: e.target.value,
                              }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") addTagToCat(cat.id);
                            }}
                            placeholder="New tag…"
                            style={{ ...INPUT, flex: 1, padding: "6px 10px" }}
                          />
                          <button
                            onClick={() => addTagToCat(cat.id)}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "9px",
                              background: cat.color + "16",
                              border: `1px solid ${cat.color}30`,
                              color: cat.color,
                              fontSize: "0.82rem",
                            }}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div
                style={{
                  borderTop: "1px solid rgba(105,138,98,0.14)",
                  paddingTop: "14px",
                }}
              >
                <FLabel>Add New Category</FLabel>
                <div
                  style={{
                    display: "flex",
                    gap: "7px",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <input
                    value={newCat.name}
                    onChange={(e) =>
                      setNewCat((p) => ({ ...p, name: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addSymptomCat();
                    }}
                    placeholder="Category name…"
                    style={{ ...INPUT, flex: 1, padding: "8px 11px" }}
                  />
                  <button
                    onClick={addSymptomCat}
                    style={{
                      padding: "8px 14px",
                      borderRadius: "10px",
                      background: "rgba(139,154,127,0.16)",
                      border: "1px solid rgba(139,154,127,0.28)",
                      color: "#556650",
                      fontSize: "0.82rem",
                      flexShrink: 0,
                    }}
                  >
                    +
                  </button>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7,1fr)",
                    gap: "6px",
                    maxWidth: "200px",
                  }}
                >
                  {CAT_COLORS.map((c) => (
                    <div
                      key={c}
                      onClick={() => setNewCat((p) => ({ ...p, color: c }))}
                      style={{
                        width: "22px",
                        height: "22px",
                        borderRadius: "50%",
                        background: c,
                        cursor: "pointer",
                        border:
                          newCat.color === c
                            ? "2.5px solid rgba(70,62,50,0.6)"
                            : "1.5px solid rgba(105,138,98,0.24)",
                        boxShadow:
                          newCat.color === c ? `0 0 6px ${c}80` : "none",
                        transition: "all 0.12s",
                      }}
                    />
                  ))}
                  <ColorWheel
                    value={newCat.color}
                    onChange={(c) => setNewCat((p) => ({ ...p, color: c }))}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Background ─────────────────────────────────────────────────────────────
  const TABS = [
    { id: "lotus", Icon: IconLotus, label: "Gastro" },
    { id: "yinyang", Icon: IconYinYang, label: "Symptoms" },
  ];

  return (
    <div
      style={{
        minHeight: "calc(100dvh + env(safe-area-inset-bottom, 34px))",
        background: coverImage
          ? "transparent"
          : "linear-gradient(180deg,#eef3e8 0%,#e7efe2 42%,#dce6d5 100%)",
        position: "relative",
      }}
    >
      {/* Soft drifting colour behind the glass (only when no cover image) */}
      {!coverImage && (
        <div className="fern-bg" aria-hidden="true">
          <div className="fern-orb fo1" />
          <div className="fern-orb fo2" />
          <div className="fern-orb fo3" />
          <div className="fern-orb fo4" />
        </div>
      )}
      {/* Fixed background layers — separate from scroll so iOS never jumps */}
      {coverImage && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: "calc(-1 * env(safe-area-inset-bottom, 34px))",
              zIndex: 0,
              backgroundImage: `url(${coverImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: "calc(-1 * env(safe-area-inset-bottom, 34px))",
              zIndex: 0,
              background: "rgba(200,192,178,0.28)",
              pointerEvents: "none",
            }}
          />
        </>
      )}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          maxWidth: "580px",
          margin: "0 auto",
          padding: isMobile ? "0 0 40px" : "0 20px 40px",
        }}
      >
        <div
          style={{
            padding: isMobile
              ? "calc(env(safe-area-inset-top, 44px) + 14px) 20px 0"
              : "44px 0 0",
            textAlign: "center",
            position: "relative",
          }}
        >
          {/* ── 3-dot cover menu — minimal, upper left ─────────────── */}
          <div
            ref={coverMenuRef}
            style={{
              position: "absolute",
              top: isMobile
                ? "calc(env(safe-area-inset-top, 44px) + 6px)"
                : "10px",
              left: isMobile ? "12px" : "0",
              zIndex: 10,
            }}
          >
            <button
              onClick={() => setShowCoverMenu((p) => !p)}
              style={{
                background: "none",
                border: "none",
                padding: "4px 6px",
                color: "rgba(130,118,100,0.5)",
                fontSize: "1.15rem",
                letterSpacing: "0.16em",
                lineHeight: 1,
                cursor: "pointer",
              }}
            >
              ···
            </button>
            {showCoverMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 5px)",
                  left: 0,
                  background: "rgba(240,246,236,0.94)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  borderRadius: "12px",
                  border: "1px solid rgba(255,255,255,0.7)",
                  boxShadow: "0 8px 22px -10px rgba(65,100,55,0.4)",
                  minWidth: "160px",
                  overflow: "hidden",
                  cursor: "default",
                }}
                className="slide-down"
              >
                <label
                  style={{
                    display: "block",
                    padding: "11px 16px",
                    fontSize: "0.8rem",
                    color: TEXT,
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(200,192,178,0.3)",
                    fontWeight: "500",
                    lineHeight: "1.3",
                    whiteSpace: "nowrap",
                  }}
                >
                  {coverImage ? "Change background" : "Upload background"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onCoverUpload}
                    style={{ display: "none" }}
                  />
                </label>
                {coverImage && (
                  <button
                    type="button"
                    onClick={removeCover}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "11px 16px",
                      fontSize: "0.8rem",
                      color: "#a06050",
                      textAlign: "left",
                      background: "none",
                      border: "none",
                      fontWeight: "500",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                    }}
                  >
                    Remove background
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Title */}
          <h1
            className={coverImage ? "app-title" : undefined}
            style={{
              fontSize: isMobile ? "1.9rem" : "2.2rem",
              fontWeight: "300",
              letterSpacing: "0.42em",
              color: coverImage ? "rgba(242,236,224,0.96)" : "#4a6548",
              textShadow: coverImage
                ? undefined
                : "0 0 28px rgba(255,255,255,0.85)",
              margin: 0,
              textTransform: "uppercase",
              paddingLeft: "0.42em",
            }}
          >
            ONE
          </h1>

          {/* Tab pill */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
              marginBottom: "22px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                gap: "4px",
                padding: "6px",
                position: "relative",
                background: coverImage
                  ? "linear-gradient(160deg,rgba(255,252,246,0.62),rgba(255,252,246,0.46))"
                  : "linear-gradient(160deg,rgba(255,252,246,0.4),rgba(255,252,246,0.14))",
                backdropFilter: coverImage
                  ? "blur(22px) saturate(140%)"
                  : "blur(12px) saturate(150%)",
                WebkitBackdropFilter: coverImage
                  ? "blur(22px) saturate(140%)"
                  : "blur(12px) saturate(150%)",
                borderRadius: "50px",
                border: "1px solid rgba(255,255,255,0.65)",
                boxShadow:
                  "0 12px 26px -16px rgba(65,100,55,0.4),0 1px 0 rgba(255,255,255,0.8) inset",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  top: "6px",
                  left: "6px",
                  width: "118px",
                  height: "46px",
                  borderRadius: "40px",
                  background: "rgba(255,255,255,0.6)",
                  boxShadow:
                    "inset 0 0 0 1px rgba(255,255,255,0.8),0 6px 16px -8px rgba(65,100,55,0.4)",
                  transform: `translateX(${
                    TABS.findIndex((t) => t.id === activeTab) * 122
                  }px)`,
                  transition: "transform 0.55s cubic-bezier(.3,1.35,.45,1)",
                }}
              />
              {TABS.map(({ id, Icon, label }) => {
                const on = activeTab === id;
                return (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`tab-btn${on ? " active" : ""}`}
                    style={{
                      width: "118px",
                      height: "46px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      borderRadius: "40px",
                    }}
                  >
                    <div
                      className=""
                      style={{
                        transition: "filter 0.35s ease",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Icon color={on ? "#364734" : "#8fa68a"} />
                    </div>
                    <span
                      style={{
                        fontSize: "0.86rem",
                        fontWeight: "400",
                        color: on ? "#364734" : "#8fa68a",
                        transition: "color 0.35s ease",
                      }}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{ padding: isMobile ? "0 14px" : "0" }}>
          {activeTab === "lotus" && renderTab1()}
          {activeTab === "yinyang" && renderTab2()}
        </div>

        {/* Export / Import */}
        <div
          style={{
            marginTop: "28px",
            display: "flex",
            justifyContent: "center",
            gap: "10px",
            opacity: 0.32,
            transition: "opacity 0.3s",
            padding: isMobile ? "0 14px" : "0",
          }}
          onMouseOver={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseOut={(e) => (e.currentTarget.style.opacity = "0.32")}
        >
          <button
            onClick={exportData}
            style={{
              padding: "6px 14px",
              background: "rgba(255,255,255,0.38)",
              color: TEXT_SM,
              borderRadius: "9px",
              border: "1px solid rgba(105,138,98,0.24)",
              fontSize: "0.7rem",
              letterSpacing: "0.03em",
            }}
          >
            Export Data
          </button>
          <label
            style={{
              padding: "6px 14px",
              background: "rgba(255,255,255,0.38)",
              color: TEXT_SM,
              borderRadius: "9px",
              border: "1px solid rgba(105,138,98,0.24)",
              cursor: "pointer",
              fontSize: "0.7rem",
              letterSpacing: "0.03em",
              display: "inline-block",
            }}
          >
            Import Data
            <input
              type="file"
              accept=".json"
              onChange={importData}
              style={{ display: "none" }}
            />
          </label>
        </div>
      </div>
    </div>
  );
}
